import { DivButton } from '../../../commons/components/DivButton';
import { DividerIcon } from '../../../assets/icons';
import { Info } from '../../../commons/components/Info';
import Dropzone, { ErrorCode, FileRejection } from 'react-dropzone';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { ProgramAttachment } from '../../types';
import { ArrayHelpers, FieldArrayRenderProps } from 'formik';
import { useNotification } from '../../../notification/notificationsContext';
import { hasErrorOfType } from '../../../util/file-utils';
import { newRenderId } from '../../../util/utils';
import { isVideo } from './util';

const THUMBNAIL_MIME_TYPE = 'image/jpeg';

const once = async (target: HTMLElement, event: string, timeoutMs: number) =>
  new Promise<void>((resolve, reject) => {
    const on = () => {
      cleanup();
      resolve();
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const err = (e: any) => {
      cleanup();
      reject(e instanceof Error ? e : new Error(String(e)));
    };
    const to = timeoutMs
      ? setTimeout(() => err(new Error(`Timeout waiting for ${event}`)), timeoutMs)
      : null;

    function cleanup() {
      target.removeEventListener(event, on);
      target.removeEventListener('error', err);
      if (to) clearTimeout(to);
    }

    target.addEventListener(event, on, { once: true });
    target.addEventListener('error', err, { once: true });
  });

export const AddAttachment = (props: {
  maxAttachments: number;
  maxSizeMb: number;
  notDeletedAttachmentsCount: number;
  arrayHelpers: FieldArrayRenderProps;
  icon: ReactElement;
  text?: string;
  hideAddIfMaxAttachments?: boolean;
  buttonClassName?: string;
  dataTest?: string;
  accept?: string[];
  onAdd?: (newAttachments: ProgramAttachment[]) => void;
}) => {
  const {
    maxAttachments,
    maxSizeMb,
    icon,
    text,
    buttonClassName,
    hideAddIfMaxAttachments,
    dataTest,
    notDeletedAttachmentsCount,
    arrayHelpers,
    accept,
    onAdd,
  } = props;
  const [attachError, setAttachError] = useState<'TOO LARGE' | 'TOO MANY' | 'WRONG FORMAT' | null>(
    null
  );
  const { notifyError } = useNotification();

  const handleAddProgramAttachment = async (
    arrayHelper: ArrayHelpers,
    files: File[],
    onAdd?: (newAttachments: ProgramAttachment[]) => void
  ) => {
    const filesToAdd = files.slice(0, maxAttachments - notDeletedAttachmentsCount);

    const newAttachments: ProgramAttachment[] = [];
    for (const file of filesToAdd) {
      let videoMetadata: { thumbnail: Blob | null; duration: number } | null = null;
      if (isVideo(file.type)) {
        const video = window.document.createElement('video');
        const canvas = window.document.createElement('canvas');
        video.preload = 'auto';
        video.muted = true;
        video.src = URL.createObjectURL(file);
        await once(video, 'loadedmetadata', 5000);

        const duration = video.duration;
        const target = isFinite(duration) && duration > 0 ? duration * 0.2 : 0;

        if (target > 0) {
          video.currentTime = target;
          await once(video, 'seeked', 5000);
        }

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);

        videoMetadata = await new Promise((r) => {
          canvas.toBlob((blob) => {
            r({
              thumbnail: blob,
              duration,
            });
          }, THUMBNAIL_MIME_TYPE);
        });
      }
      newAttachments.push({
        id: newRenderId(),
        name: file.name,
        contentType: file.type,
        status: 'NEW',
        file: file,
        thumbnail: videoMetadata?.thumbnail,
      });
    }
    newAttachments.forEach((attachment) => {
      arrayHelper.push(attachment);
    });
    if (onAdd) onAdd(newAttachments);
    if (filesToAdd.length < files.length) {
      setAttachError('TOO MANY');
    } else {
      setAttachError(null);
    }
  };

  const onAttachmentFailed = useCallback(
    (fileRejections: FileRejection[]) => {
      if (hasErrorOfType(fileRejections, ErrorCode.FileTooLarge)) {
        setAttachError('TOO LARGE');
        notifyError({
          notificationMsg: `The file that you tried to attach is too big. The maximum file size is ${maxSizeMb} MB.`,
        });
      } else if (hasErrorOfType(fileRejections, ErrorCode.TooManyFiles)) {
        setAttachError('TOO MANY');
        notifyError({
          notificationMsg: `You tried to attach too many files. The maximum number of attachments in a program is ${maxAttachments}.`,
        });
      } else if (hasErrorOfType(fileRejections, ErrorCode.FileInvalidType)) {
        setAttachError('WRONG FORMAT');
        const extensions = `${accept?.join(', ')}`;
        notifyError({
          notificationMsg: `You tried to attach a file of the wrong format. Only the following file extensions are expected: ${extensions}.`,
        });
      }
    },
    [notifyError, accept, maxSizeMb, maxAttachments]
  );

  useEffect(() => {
    if (attachError === 'TOO MANY' && notDeletedAttachmentsCount < maxAttachments) {
      setAttachError(null);
    }
  }, [notDeletedAttachmentsCount, attachError, maxAttachments]);

  return (
    <Dropzone
      onDrop={async (acceptedFiles) =>
        handleAddProgramAttachment(arrayHelpers, acceptedFiles, onAdd)
      }
      multiple={true}
      noClick={true}
      noKeyboard={true}
      maxFiles={maxAttachments}
      maxSize={maxSizeMb * 1024 * 1024}
      onDropRejected={onAttachmentFailed}
      accept={accept}
    >
      {({ getRootProps, getInputProps, open }) => (
        <div {...getRootProps({ className: 'dropzone' })}>
          <input {...getInputProps()} data-test="program-edit__input-file-attachment" />
          {(notDeletedAttachmentsCount < maxAttachments || !hideAddIfMaxAttachments) && (
            <div className="attachments-table__add-btn-wrapper">
              <DivButton
                className={buttonClassName}
                icon={icon}
                text={text}
                dataTest={dataTest}
                onClick={open}
              />
              {attachError === null && (
                <div data-test="program-edit__attachment-info">
                  <Info
                    tooltipText={`Allowed to upload up to ${maxAttachments} attachments up to ${maxSizeMb} MB each`}
                    tooltipPlace="right"
                    tooltipWidth={136}
                  />
                </div>
              )}
              {attachError !== null && (
                <>
                  <DividerIcon
                    className="flex-shrink-0"
                    style={{ marginLeft: '16px', marginRight: '16px' }}
                  />
                  <span
                    data-test={
                      attachError === 'TOO MANY'
                        ? 'program-edit__error-text--too-many'
                        : attachError === 'TOO LARGE'
                        ? 'program-edit__error-text--too-large'
                        : 'program-edit__error-text--wrong-format'
                    }
                    className="growegy-label14--red-500"
                  >
                    {attachError === 'TOO MANY'
                      ? `Not allowed to upload more than ${maxAttachments} attachments`
                      : attachError === 'TOO LARGE'
                      ? `Not allowed to upload files bigger than ${maxSizeMb} MB`
                      : `Only ${accept?.join(', ')} are allowed`}
                  </span>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </Dropzone>
  );
};
